import React, { useRef, useEffect, useMemo, useState, useCallback } from 'react';
import { DataGrid, DataGridProps, GridColDef, GridRowsProp, useGridApiRef, GridCellParams } from '@mui/x-data-grid';
import { GridApiCommunity } from '@mui/x-data-grid/internals';
import { BUFFER_STAGES, DEFAULT_ROW_BUFFER, DEFAULT_COLUMN_BUFFER, SCROLL_LOCK_DURATION, ScrollSource, VISIBILITY_THRESHOLD } from './freezePaneConfigurationConsts';
import { frozenCornerSx, frozenColumnsSx, frozenRowsSx, mainGridSx } from './FreezePaneDataGridSx';
import './FreezePaneDataGrid.scss';
import '../../components/EditableTable/EditableTable.scss';

interface FreezePaneDataGridProps extends Omit<DataGridProps, 'columns' | 'rows'> {
    columns: GridColDef[];
    rows: GridRowsProp;
    frozenColumnCount?: number;
    frozenRowCount?: number;
    frozenColumnStart?: number;
    frozenRowStart?: number;
    scrollableColumnStart?: number;
    scrollableRowStart?: number;
    freezeFromVisible?: boolean;
    onFrozenColumnCountChange?: (count: number) => void;
    onFrozenRowCountChange?: (count: number) => void;
    onCellSelectionChange?: (cell: GridCellParams | null, event?: React.MouseEvent) => void;
    onGetVisibleRanges?: (getRanges: () => { firstColumnIndex: number; lastColumnIndex: number; firstRowIndex: number; lastRowIndex: number }) => void;
    onGetScrollPosition?: (getPosition: () => { scrollTop: number; scrollLeft: number } | null) => void;
    scrollToCell?: { id: any; field: string; viewportOffset?: { top: number; left: number } } | null;
    useDomBasedRanges?: boolean;
    mainApiRef?: React.MutableRefObject<GridApiCommunity>;
}

const FreezePaneDataGrid: React.FC<FreezePaneDataGridProps> = ({
    columns,
    rows,
    frozenColumnCount = 0,
    frozenRowCount = 0,
    frozenColumnStart: externalFrozenColumnStart,
    frozenRowStart: externalFrozenRowStart,
    scrollableColumnStart: externalScrollableColumnStart,
    scrollableRowStart: externalScrollableRowStart,
    freezeFromVisible = false,
    rowHeight = 52,
    columnHeaderHeight = 34,
    onFrozenColumnCountChange,
    onFrozenRowCountChange,
    onCellSelectionChange,
    onGetVisibleRanges,
    onGetScrollPosition,
    scrollToCell,
    onCellClick: externalOnCellClick,
    mainApiRef: externalMainApiRef,
    ...otherProps
}) => {
    const internalMainGridApiRef = useGridApiRef();
    const mainGridApiRef = externalMainApiRef ?? internalMainGridApiRef;
    const frozenColumnsApiRef = useGridApiRef();
    const frozenRowsApiRef = useGridApiRef();
    const frozenCornerApiRef = useGridApiRef();

    const containerRef = useRef<HTMLDivElement>(null);

    const mainScrollerRef = useRef<HTMLElement | null>(null);
    const frozenColsScrollerRef = useRef<HTMLElement | null>(null);
    const frozenRowsScrollerRef = useRef<HTMLElement | null>(null);

    const rafIdRef = useRef<number | null>(null);
    const scrollLockRef = useRef<{ source: ScrollSource; timestamp: number } | null>(null);
    const lastSyncedPositionRef = useRef<{ scrollTop: number; scrollLeft: number }>({ scrollTop: 0, scrollLeft: 0 });

    const [currentColumnBuffer, setCurrentColumnBuffer] = useState(DEFAULT_COLUMN_BUFFER);
    const [currentRowBuffer, setCurrentRowBuffer] = useState(DEFAULT_ROW_BUFFER);
    const prevFrozenCountsRef = useRef({ frozenColumnCount, frozenRowCount });

    const [selectedCell, setSelectedCell] = useState<GridCellParams | null>(null);

    const [autoFrozenColumnStart, setAutoFrozenColumnStart] = useState<number | null>(null);
    const [autoFrozenRowStart, setAutoFrozenRowStart] = useState<number | null>(null);
    const [autoScrollableColumnStart, setAutoScrollableColumnStart] = useState<number | null>(null);
    const [autoScrollableRowStart, setAutoScrollableRowStart] = useState<number | null>(null);
    const capturedVisibleRangeRef = useRef<{ firstColumnIndex: number; lastColumnIndex: number; firstRowIndex: number; lastRowIndex: number } | null>(null);

    const frozenColumnStart = freezeFromVisible && autoFrozenColumnStart !== null ? autoFrozenColumnStart : (externalFrozenColumnStart ?? 0);
    const frozenRowStart = freezeFromVisible && autoFrozenRowStart !== null ? autoFrozenRowStart : (externalFrozenRowStart ?? 0);
    const scrollableColumnStart = freezeFromVisible && autoScrollableColumnStart !== null ? autoScrollableColumnStart : externalScrollableColumnStart;
    const scrollableRowStart = freezeFromVisible && autoScrollableRowStart !== null ? autoScrollableRowStart : externalScrollableRowStart;

    const detectVisibleRangesFromDom = useCallback(() => {
        const container = containerRef.current;
        if (!container) return null;

        const gridRoot = container.querySelector('.freeze-pane-main .MuiDataGrid-root') 
            || container.querySelector('.freeze-pane-simple .MuiDataGrid-root')
            || container.querySelector('.MuiDataGrid-root');
        if (!gridRoot) return null;

        const virtualScroller = gridRoot.querySelector('.MuiDataGrid-virtualScroller') as HTMLElement;
        if (!virtualScroller) return null;

        const scrollerRect = virtualScroller.getBoundingClientRect();
        const viewportTop = scrollerRect.top;
        const viewportBottom = viewportTop + virtualScroller.clientHeight;
        const viewportLeft = scrollerRect.left;
        const viewportRight = viewportLeft + virtualScroller.clientWidth;

        const renderZone = gridRoot.querySelector('.MuiDataGrid-virtualScrollerRenderZone');
        if (!renderZone) return null;

        const allRenderedRows = Array.from(renderZone.querySelectorAll('.MuiDataGrid-row'));
        const visibleRowElements = allRenderedRows.filter((row) => {
            const rowRect = row.getBoundingClientRect();
            return rowRect.bottom > viewportTop && rowRect.top < viewportBottom;
        });

        if (visibleRowElements.length === 0) return null;

        const rowIndices: number[] = [];
        visibleRowElements.forEach((row) => {
            const rowId = row.getAttribute('data-id');
            if (rowId) {
                const index = rows.findIndex(r => {
                    const id = r.id !== undefined ? r.id : (r as any).gridId;
                    return String(id) === rowId;
                });
                if (index >= 0) {
                    rowIndices.push(index);
                }
            }
        });

        if (rowIndices.length === 0) return null;

        const firstVisibleRowIndex = Math.min(...rowIndices);
        const lastVisibleRowIndex = Math.max(...rowIndices);

        const firstRow = visibleRowElements[0];
        const allCells = Array.from(firstRow.querySelectorAll('.MuiDataGrid-cell'));
        const visibleCellElements = allCells.filter((cell) => {
            const cellRect = cell.getBoundingClientRect();
            return cellRect.right > viewportLeft && cellRect.left < viewportRight;
        });

        const columnIndices: number[] = [];
        visibleCellElements.forEach((cell) => {
            const field = cell.getAttribute('data-field');
            if (field) {
                const index = columns.findIndex(col => col.field === field);
                if (index >= 0) {
                    columnIndices.push(index);
                }
            }
        });

        if (columnIndices.length === 0) return null;

        const firstVisibleColIndex = Math.min(...columnIndices);
        const lastVisibleColIndex = Math.max(...columnIndices);

        return {
            firstColumnIndex: firstVisibleColIndex,
            lastColumnIndex: lastVisibleColIndex,
            firstRowIndex: firstVisibleRowIndex,
            lastRowIndex: lastVisibleRowIndex
        };
    }, [columns, rows]);

    useEffect(() => {
        const prevCounts = prevFrozenCountsRef.current;
        const frozenCountsChanged =
            prevCounts.frozenColumnCount !== frozenColumnCount ||
            prevCounts.frozenRowCount !== frozenRowCount;

        if (frozenCountsChanged && freezeFromVisible) {
            const columnCountChanging = prevCounts.frozenColumnCount !== frozenColumnCount;
            const rowCountChanging = prevCounts.frozenRowCount !== frozenRowCount;
            const isFirstColumnFreeze = columnCountChanging && prevCounts.frozenColumnCount === 0 && frozenColumnCount > 0;
            const isFirstRowFreeze = rowCountChanging && prevCounts.frozenRowCount === 0 && frozenRowCount > 0;
            const isColumnFreezeAdjust = columnCountChanging && prevCounts.frozenColumnCount > 0 && frozenColumnCount > 0;
            const isRowFreezeAdjust = rowCountChanging && prevCounts.frozenRowCount > 0 && frozenRowCount > 0;
            const unfreezeColumns = columnCountChanging && frozenColumnCount === 0;
            const unfreezeRows = rowCountChanging && frozenRowCount === 0;

            if (isFirstColumnFreeze || isFirstRowFreeze) {
                const visibleRanges = capturedVisibleRangeRef.current || detectVisibleRangesFromDom();
                
                if (visibleRanges) {
                    if (isFirstColumnFreeze) {
                        const lastVisibleCol = visibleRanges.lastColumnIndex;
                        const colsToFreeze = Math.min(frozenColumnCount, lastVisibleCol - visibleRanges.firstColumnIndex + 1);
                        const freezeStartCol = Math.max(0, lastVisibleCol - colsToFreeze + 1);
                        setAutoFrozenColumnStart(freezeStartCol);
                        setAutoScrollableColumnStart(lastVisibleCol + 1);
                    }
                    
                    if (isFirstRowFreeze) {
                        const lastVisibleRow = visibleRanges.lastRowIndex;
                        const rowsToFreeze = Math.min(frozenRowCount, lastVisibleRow - visibleRanges.firstRowIndex + 1);
                        const freezeStartRow = Math.max(0, lastVisibleRow - rowsToFreeze + 1);
                        setAutoFrozenRowStart(freezeStartRow);
                        setAutoScrollableRowStart(lastVisibleRow + 1);
                    }
                }
            }

            if (isColumnFreezeAdjust && autoScrollableColumnStart !== null) {
                const freezeEndCol = autoScrollableColumnStart - 1;
                const newFreezeStartCol = Math.max(0, freezeEndCol - frozenColumnCount + 1);
                setAutoFrozenColumnStart(newFreezeStartCol);
            }

            if (isRowFreezeAdjust && autoScrollableRowStart !== null) {
                const freezeEndRow = autoScrollableRowStart - 1;
                const newFreezeStartRow = Math.max(0, freezeEndRow - frozenRowCount + 1);
                setAutoFrozenRowStart(newFreezeStartRow);
            }

            if (unfreezeColumns) {
                setAutoFrozenColumnStart(null);
                setAutoScrollableColumnStart(null);
            }
            if (unfreezeRows) {
                setAutoFrozenRowStart(null);
                setAutoScrollableRowStart(null);
            }

            capturedVisibleRangeRef.current = null;
        }

        prevFrozenCountsRef.current = { frozenColumnCount, frozenRowCount };

        if (frozenCountsChanged) {
            setCurrentColumnBuffer(DEFAULT_COLUMN_BUFFER);
            setCurrentRowBuffer(DEFAULT_ROW_BUFFER);
            return;
        }

        const timeouts: ReturnType<typeof setTimeout>[] = [];

        BUFFER_STAGES.forEach((stage, index) => {
            if (index === 0) return;

            const timeout = setTimeout(() => {
                const apply = () => {
                    setCurrentColumnBuffer(stage.columnBuffer);
                    setCurrentRowBuffer(stage.rowBuffer);
                };

                if ('requestIdleCallback' in window) {
                    (window as Window & { requestIdleCallback: (cb: () => void) => number }).requestIdleCallback(apply);
                } else {
                    apply();
                }
            }, stage.delay);

            timeouts.push(timeout);
        });

        const finalTimeout = setTimeout(() => {
            const apply = () => {
                setCurrentColumnBuffer(DEFAULT_COLUMN_BUFFER);
                setCurrentRowBuffer(DEFAULT_ROW_BUFFER);
            };

            if ('requestIdleCallback' in window) {
                (window as Window & { requestIdleCallback: (cb: () => void) => number }).requestIdleCallback(apply);
            } else {
                apply();
            }
        }, 700);

        timeouts.push(finalTimeout);

        return () => {
            timeouts.forEach(clearTimeout);
        };
    }, [frozenColumnCount, frozenRowCount, freezeFromVisible, detectVisibleRangesFromDom]);

    const frozenColumns = useMemo(() => {
        const cols = frozenColumnStart > 0
            ? columns.slice(frozenColumnStart, frozenColumnStart + frozenColumnCount)
            : columns.slice(0, frozenColumnCount);
        return cols.map(col => {
            const { flex, minWidth, maxWidth, ...rest } = col;
            return { ...rest, width: col.width || 150, resizable: false };
        });
    }, [columns, frozenColumnCount, frozenColumnStart]);

    const scrollableColumns = useMemo(() => {
        if (scrollableColumnStart !== undefined) {
            return columns.slice(scrollableColumnStart);
        }
        if (frozenColumnStart > 0) {
            return columns.slice(frozenColumnStart + frozenColumnCount);
        }
        return columns.slice(frozenColumnCount);
    }, [columns, frozenColumnCount, frozenColumnStart, scrollableColumnStart]);

    const frozenRows = useMemo(() => {
        if (frozenRowStart > 0) {
            return rows.slice(frozenRowStart, frozenRowStart + frozenRowCount);
        }
        return rows.slice(0, frozenRowCount);
    }, [rows, frozenRowCount, frozenRowStart]);

    const scrollableRows = useMemo(() => {
        if (scrollableRowStart !== undefined) {
            return rows.slice(scrollableRowStart);
        }
        if (frozenRowStart > 0) {
            return rows.slice(frozenRowStart + frozenRowCount);
        }
        return rows.slice(frozenRowCount);
    }, [rows, frozenRowCount, frozenRowStart, scrollableRowStart]);

    const frozenColumnsWidth = useMemo(() => {
        return frozenColumns.reduce((sum, col) => sum + (col.width || 150), 0);
    }, [frozenColumns]);

    const frozenRowsHeight = useMemo(() => {
        return columnHeaderHeight + (frozenRows.length * rowHeight);
    }, [frozenRows.length, rowHeight, columnHeaderHeight]);

    const syncScrollPositions = useCallback((source: ScrollSource, scrollTop: number, scrollLeft: number) => {
        const mainScroller = mainScrollerRef.current;
        const frozenColsScroller = frozenColsScrollerRef.current;
        const frozenRowsScroller = frozenRowsScrollerRef.current;

        if (source === ScrollSource.Main || source === ScrollSource.FrozenCols) {
            if (frozenColsScroller && source !== ScrollSource.FrozenCols) {
                frozenColsScroller.scrollTo({ top: scrollTop, behavior: 'instant' as ScrollBehavior });
            }
            if (mainScroller && source !== ScrollSource.Main) {
                mainScroller.scrollTo({ top: scrollTop, behavior: 'instant' as ScrollBehavior });
            }
        }

        if (source === ScrollSource.Main || source === ScrollSource.FrozenRows) {
            if (frozenRowsScroller && source !== ScrollSource.FrozenRows) {
                frozenRowsScroller.scrollLeft = scrollLeft;
            }
            if (mainScroller && source !== ScrollSource.Main) {
                mainScroller.scrollLeft = scrollLeft;
            }
        }

        lastSyncedPositionRef.current = { scrollTop, scrollLeft };
    }, []);

    const forceAlignmentSync = useCallback(() => {
        const mainScroller = mainScrollerRef.current;
        if (!mainScroller) return;

        const { scrollTop, scrollLeft } = mainScroller;
        const frozenColsScroller = frozenColsScrollerRef.current;
        const frozenRowsScroller = frozenRowsScrollerRef.current;

        if (frozenColsScroller && Math.abs(frozenColsScroller.scrollTop - scrollTop) > 0.5) {
            frozenColsScroller.scrollTo({ top: scrollTop, behavior: 'instant' as ScrollBehavior });
        }
        if (frozenRowsScroller && Math.abs(frozenRowsScroller.scrollLeft - scrollLeft) > 0.5) {
            frozenRowsScroller.scrollLeft = scrollLeft;
        }

        lastSyncedPositionRef.current = { scrollTop, scrollLeft };
    }, []);

    const columnWidthCache = useMemo(() => {
        const widths: number[] = [];
        const positions: number[] = [];
        let accumulatedWidth = 0;

        columns.forEach((col) => {
            positions.push(accumulatedWidth);
            const width = col.width || col.minWidth || 150;
            widths.push(width);
            accumulatedWidth += width;
        });

        return { widths, positions, totalWidth: accumulatedWidth };
    }, [columns]);

    const getVisibleRanges = useCallback(() => {
        const container = containerRef.current;
        if (!container) {
            return { firstColumnIndex: 0, lastColumnIndex: 0, firstRowIndex: 0, lastRowIndex: 0 };
        }

        let mainScroller = mainScrollerRef.current;
        
        if (!mainScroller) {
            mainScroller = container.querySelector('.freeze-pane-main .MuiDataGrid-virtualScroller') as HTMLElement
                || container.querySelector('.freeze-pane-simple .MuiDataGrid-virtualScroller') as HTMLElement
                || container.querySelector('.MuiDataGrid-virtualScroller') as HTMLElement;
        }

        if (!mainScroller) {
            return { firstColumnIndex: 0, lastColumnIndex: 0, firstRowIndex: 0, lastRowIndex: 0 };
        }

        const scrollLeft = mainScroller.scrollLeft;
        const scrollTop = mainScroller.scrollTop;
        const viewportWidth = mainScroller.clientWidth;
        const viewportHeight = mainScroller.clientHeight;

        const hasFrozenCols = frozenColumnCount > 0;
        const hasFrozenRows = frozenRowCount > 0;

        let scrollableColIndices: number[] = [];
        if (hasFrozenCols) {
            if (frozenColumnStart > 0) {
                for (let i = 0; i < frozenColumnStart; i++) scrollableColIndices.push(i);
                for (let i = frozenColumnStart + frozenColumnCount; i < columns.length; i++) scrollableColIndices.push(i);
            } else {
                for (let i = frozenColumnCount; i < columns.length; i++) scrollableColIndices.push(i);
            }
        } else {
            for (let i = 0; i < columns.length; i++) scrollableColIndices.push(i);
        }

        let scrollableColPositions: number[] = [];
        let accWidth = 0;
        scrollableColIndices.forEach((colIdx) => {
            scrollableColPositions.push(accWidth);
            accWidth += columnWidthCache.widths[colIdx];
        });

        let firstVisibleColLocalIdx = -1;
        let lastVisibleColLocalIdx = -1;
        const visibleColDetails: { localIdx: number; colIdx: number; colWidth: number; visibleWidth: number; visiblePercent: string }[] = [];
        const allColDetails: { colIdx: number; colStart: number; colEnd: number; colWidth: number; visibleWidth: number; visiblePercent: string; meetsThreshold: boolean }[] = [];

        for (let localIdx = 0; localIdx < scrollableColIndices.length; localIdx++) {
            const colIdx = scrollableColIndices[localIdx];
            const colStart = scrollableColPositions[localIdx];
            const colWidth = columnWidthCache.widths[colIdx];
            const colEnd = colStart + colWidth;

            const visibleStart = Math.max(colStart, scrollLeft);
            const visibleEnd = Math.min(colEnd, scrollLeft + viewportWidth);
            const visibleWidth = Math.max(0, visibleEnd - visibleStart);
            const visibleRatio = visibleWidth / colWidth;
            const visiblePercent = `${(visibleRatio * 100).toFixed(1)}%`;
            const meetsThreshold = visibleRatio >= VISIBILITY_THRESHOLD;

            if (visibleWidth > 0 || (colStart >= scrollLeft && colStart <= scrollLeft + viewportWidth)) {
                allColDetails.push({ colIdx, colStart, colEnd, colWidth, visibleWidth, visiblePercent, meetsThreshold });
            }

            if (meetsThreshold) {
                visibleColDetails.push({ localIdx, colIdx, colWidth, visibleWidth, visiblePercent });
                if (firstVisibleColLocalIdx === -1) {
                    firstVisibleColLocalIdx = localIdx;
                }
                lastVisibleColLocalIdx = localIdx;
            } else if (firstVisibleColLocalIdx !== -1 && colStart > scrollLeft + viewportWidth) {
                break;
            }
        }

        if (firstVisibleColLocalIdx === -1) firstVisibleColLocalIdx = 0;
        if (lastVisibleColLocalIdx === -1) lastVisibleColLocalIdx = Math.max(0, scrollableColIndices.length - 1);

        const firstColumnIndex = scrollableColIndices[firstVisibleColLocalIdx] ?? 0;
        const lastColumnIndex = scrollableColIndices[lastVisibleColLocalIdx] ?? 0;

        let scrollableRowIndices: number[] = [];
        if (hasFrozenRows) {
            if (frozenRowStart > 0) {
                for (let i = 0; i < frozenRowStart; i++) scrollableRowIndices.push(i);
                for (let i = frozenRowStart + frozenRowCount; i < rows.length; i++) scrollableRowIndices.push(i);
            } else {
                for (let i = frozenRowCount; i < rows.length; i++) scrollableRowIndices.push(i);
            }
        } else {
            for (let i = 0; i < rows.length; i++) scrollableRowIndices.push(i);
        }


        let firstVisibleRowLocalIdx = -1;
        let lastVisibleRowLocalIdx = -1;
        const visibleRowDetails: { localIdx: number; rowIdx: number; rowHeight: number; visibleHeight: number; visiblePercent: string }[] = [];
        const allRowDetails: { rowIdx: number; rowStart: number; rowEnd: number; rowHeight: number; visibleHeight: number; visiblePercent: string; meetsThreshold: boolean }[] = [];

        for (let localIdx = 0; localIdx < scrollableRowIndices.length; localIdx++) {
            const rowIdx = scrollableRowIndices[localIdx];
            const rowStart = localIdx * rowHeight;
            const rowEnd = rowStart + rowHeight;

            const visibleStart = Math.max(rowStart, scrollTop);
            const visibleEnd = Math.min(rowEnd, scrollTop + viewportHeight);
            const visibleHeight = Math.max(0, visibleEnd - visibleStart);
            const visibleRatio = visibleHeight / rowHeight;
            const visiblePercent = `${(visibleRatio * 100).toFixed(1)}%`;
            const meetsThreshold = visibleRatio >= VISIBILITY_THRESHOLD;

            if (visibleHeight > 0 || (rowStart >= scrollTop && rowStart <= scrollTop + viewportHeight)) {
                allRowDetails.push({ rowIdx, rowStart, rowEnd, rowHeight, visibleHeight, visiblePercent, meetsThreshold });
            }

            if (meetsThreshold) {
                visibleRowDetails.push({ localIdx, rowIdx, rowHeight, visibleHeight, visiblePercent });
                if (firstVisibleRowLocalIdx === -1) {
                    firstVisibleRowLocalIdx = localIdx;
                }
                lastVisibleRowLocalIdx = localIdx;
            } else if (firstVisibleRowLocalIdx !== -1 && rowStart > scrollTop + viewportHeight) {
                break;
            }
        }

        if (firstVisibleRowLocalIdx === -1) firstVisibleRowLocalIdx = 0;
        if (lastVisibleRowLocalIdx === -1) lastVisibleRowLocalIdx = Math.max(0, scrollableRowIndices.length - 1);

        const firstRowIndex = scrollableRowIndices[firstVisibleRowLocalIdx] ?? 0;
        const lastRowIndex = scrollableRowIndices[lastVisibleRowLocalIdx] ?? 0;

        return {
            firstColumnIndex: Math.max(0, Math.min(firstColumnIndex, columns.length - 1)),
            lastColumnIndex: Math.max(0, Math.min(lastColumnIndex, columns.length - 1)),
            firstRowIndex: Math.max(0, Math.min(firstRowIndex, rows.length - 1)),
            lastRowIndex: Math.max(0, Math.min(lastRowIndex, rows.length - 1)),
        };
    }, [columns, rows, rowHeight, frozenColumnStart, frozenColumnCount, frozenRowStart, frozenRowCount, columnWidthCache]);

    const getVisibleRangesFromDom = useCallback(() => {
        const mainScroller = mainScrollerRef.current;
        if (!mainScroller || !containerRef.current) {
            return { firstColumnIndex: 0, lastColumnIndex: 0, firstRowIndex: 0, lastRowIndex: 0 };
        }

        const mainGrid = containerRef.current.querySelector('.freeze-pane-main .MuiDataGrid-root');
        if (!mainGrid) {
            return getVisibleRanges();
        }

        const virtualScroller = mainGrid.querySelector('.MuiDataGrid-virtualScroller') as HTMLElement;
        if (!virtualScroller) {
            return getVisibleRanges();
        }

        const scrollerRect = virtualScroller.getBoundingClientRect();
        const viewportTop = scrollerRect.top;
        const viewportBottom = viewportTop + virtualScroller.clientHeight;
        const viewportLeft = scrollerRect.left;
        const viewportRight = viewportLeft + virtualScroller.clientWidth;

        const renderZone = mainGrid.querySelector('.MuiDataGrid-virtualScrollerRenderZone');
        if (!renderZone) {
            return getVisibleRanges();
        }

        const allRenderedRows = Array.from(renderZone.querySelectorAll('.MuiDataGrid-row'));
        const visibleRows = allRenderedRows.filter((row) => {
            const rowRect = row.getBoundingClientRect();
            return rowRect.bottom > viewportTop && rowRect.top < viewportBottom;
        });

        if (visibleRows.length === 0) {
            return getVisibleRanges();
        }

        const scrollableRows = frozenRowStart > 0
            ? [
                ...rows.slice(0, frozenRowStart),
                ...rows.slice(frozenRowStart + frozenRowCount)
            ]
            : rows.slice(frozenRowCount);

        const rowIndices: number[] = [];
        visibleRows.forEach((row) => {
            const rowId = row.getAttribute('data-id');
            if (rowId) {
                const index = scrollableRows.findIndex(r => {
                    const id = r.id !== undefined ? r.id : (r as any).gridId;
                    return String(id) === rowId;
                });
                if (index >= 0) {
                    rowIndices.push(index);
                }
            }
        });

        if (rowIndices.length === 0) {
            return getVisibleRanges();
        }

        const firstVisibleScrollableRowIndex = Math.min(...rowIndices);
        const lastVisibleScrollableRowIndex = Math.max(...rowIndices);

        const scrollableCols = frozenColumnStart > 0
            ? [
                ...columns.slice(0, frozenColumnStart),
                ...columns.slice(frozenColumnStart + frozenColumnCount)
            ]
            : columns.slice(frozenColumnCount);

        const firstRow = visibleRows[0];
        const allCells = Array.from(firstRow.querySelectorAll('.MuiDataGrid-cell'));
        const visibleCells = allCells.filter((cell) => {
            const cellRect = cell.getBoundingClientRect();
            return cellRect.right > viewportLeft && cellRect.left < viewportRight;
        });

        const columnIndices: number[] = [];
        visibleCells.forEach((cell) => {
            const field = cell.getAttribute('data-field');
            if (field) {
                const index = scrollableCols.findIndex(col => col.field === field);
                if (index >= 0) {
                    columnIndices.push(index);
                }
            }
        });

        if (columnIndices.length === 0) {
            return getVisibleRanges();
        }

        const firstVisibleScrollableColIndex = Math.min(...columnIndices);
        const lastVisibleScrollableColIndex = Math.max(...columnIndices);

        let firstColumnIndex: number;
        let lastColumnIndex: number;
        
        if (frozenColumnStart > 0) {
            if (firstVisibleScrollableColIndex < frozenColumnStart) {
                firstColumnIndex = firstVisibleScrollableColIndex;
            } else {
                firstColumnIndex = firstVisibleScrollableColIndex + frozenColumnCount;
            }
            
            if (lastVisibleScrollableColIndex < frozenColumnStart) {
                lastColumnIndex = lastVisibleScrollableColIndex;
            } else {
                lastColumnIndex = lastVisibleScrollableColIndex + frozenColumnCount;
            }
        } else if (frozenColumnCount > 0) {
            firstColumnIndex = firstVisibleScrollableColIndex + frozenColumnCount;
            lastColumnIndex = lastVisibleScrollableColIndex + frozenColumnCount;
        } else {
            firstColumnIndex = firstVisibleScrollableColIndex;
            lastColumnIndex = lastVisibleScrollableColIndex;
        }

        let firstRowIndex: number;
        let lastRowIndex: number;

        if (frozenRowStart > 0) {
            if (firstVisibleScrollableRowIndex < frozenRowStart) {
                firstRowIndex = firstVisibleScrollableRowIndex;
            } else {
                firstRowIndex = firstVisibleScrollableRowIndex + frozenRowCount;
            }
            
            if (lastVisibleScrollableRowIndex < frozenRowStart) {
                lastRowIndex = lastVisibleScrollableRowIndex;
            } else {
                lastRowIndex = lastVisibleScrollableRowIndex + frozenRowCount;
            }
        } else if (frozenRowCount > 0) {
            firstRowIndex = firstVisibleScrollableRowIndex + frozenRowCount;
            lastRowIndex = lastVisibleScrollableRowIndex + frozenRowCount;
        } else {
            firstRowIndex = firstVisibleScrollableRowIndex;
            lastRowIndex = lastVisibleScrollableRowIndex;
        }

        firstColumnIndex = Math.max(0, Math.min(firstColumnIndex, columns.length - 1));
        lastColumnIndex = Math.max(0, Math.min(lastColumnIndex, columns.length - 1));
        firstRowIndex = Math.max(0, Math.min(firstRowIndex, rows.length - 1));
        lastRowIndex = Math.max(0, Math.min(lastRowIndex, rows.length - 1));

        return { firstColumnIndex, lastColumnIndex, firstRowIndex, lastRowIndex };
    }, [columns, rows, frozenColumnStart, frozenColumnCount, frozenRowStart, frozenRowCount, getVisibleRanges]);

    const onGetVisibleRangesRef = useRef(onGetVisibleRanges);
    onGetVisibleRangesRef.current = onGetVisibleRanges;

    useEffect(() => {
        if (onGetVisibleRangesRef.current) {
            onGetVisibleRangesRef.current(getVisibleRanges);
        }
    }, [getVisibleRanges]);

    const getScrollPosition = useCallback(() => {
        const mainScroller = mainScrollerRef.current;
        if (!mainScroller) {
            return null;
        }
        return {
            scrollTop: mainScroller.scrollTop,
            scrollLeft: mainScroller.scrollLeft
        };
    }, []);

    const onGetScrollPositionRef = useRef(onGetScrollPosition);
    onGetScrollPositionRef.current = onGetScrollPosition;

    useEffect(() => {
        if (onGetScrollPositionRef.current) {
            onGetScrollPositionRef.current(getScrollPosition);
        }
    }, [getScrollPosition]);

    const handleCellClick = useCallback((params: GridCellParams, event?: React.MouseEvent, details?: any) => {
        setSelectedCell(params);
        if (onCellSelectionChange) {
            onCellSelectionChange(params, event);
        }
        if (externalOnCellClick) {
            (externalOnCellClick as any)(params, event, details);
        }
    }, [externalOnCellClick, onCellSelectionChange]);

    useEffect(() => {
        if (!freezeFromVisible) return;

        const container = containerRef.current;
        if (!container) return;

        let captureRafId: number | null = null;
        let lastCaptureTime = 0;
        const CAPTURE_THROTTLE = 100;

        const captureOnScroll = () => {
            const now = performance.now();
            if (now - lastCaptureTime < CAPTURE_THROTTLE) return;
            
            if (captureRafId !== null) {
                cancelAnimationFrame(captureRafId);
            }

            captureRafId = requestAnimationFrame(() => {
                lastCaptureTime = performance.now();
                const ranges = detectVisibleRangesFromDom();
                if (ranges) {
                    capturedVisibleRangeRef.current = ranges;
                }
                captureRafId = null;
            });
        };

        const initialCapture = () => {
            const ranges = detectVisibleRangesFromDom();
            if (ranges) {
                capturedVisibleRangeRef.current = ranges;
            }
        };

        const findAndAttachScrollListeners = () => {
            const scrollers = container.querySelectorAll('.MuiDataGrid-virtualScroller');
            if (scrollers.length === 0) return false;

            scrollers.forEach(scroller => {
                scroller.addEventListener('scroll', captureOnScroll, { passive: true });
            });

            setTimeout(initialCapture, 50);

            return true;
        };

        if (!findAndAttachScrollListeners()) {
            const observer = new MutationObserver(() => {
                if (findAndAttachScrollListeners()) {
                    observer.disconnect();
                }
            });
            observer.observe(container, { childList: true, subtree: true });

            return () => {
                observer.disconnect();
                if (captureRafId !== null) {
                    cancelAnimationFrame(captureRafId);
                }
            };
        }

        return () => {
            if (captureRafId !== null) {
                cancelAnimationFrame(captureRafId);
            }
            const scrollers = container.querySelectorAll('.MuiDataGrid-virtualScroller');
            scrollers.forEach(scroller => {
                scroller.removeEventListener('scroll', captureOnScroll);
            });
        };
    }, [freezeFromVisible, detectVisibleRangesFromDom]);

    useEffect(() => {
        mainScrollerRef.current = null;
        frozenColsScrollerRef.current = null;
        frozenRowsScrollerRef.current = null;
        if (rafIdRef.current !== null) {
            cancelAnimationFrame(rafIdRef.current);
            rafIdRef.current = null;
        }
        scrollLockRef.current = null;
        lastSyncedPositionRef.current = { scrollTop: 0, scrollLeft: 0 };
    }, [frozenColumnCount, frozenRowCount]);

    useEffect(() => {
        if (!scrollToCell) return;
        
        const scrollToCellTopLeft = () => {
            if (!mainGridApiRef.current) {
                return false;
            }

            try {
                const rowIndex = rows.findIndex(row => {
                    const id = row.id !== undefined ? row.id : (row as any).gridId;
                    return id === scrollToCell.id;
                });
                
                const columnIndex = columns.findIndex(col => col.field === scrollToCell.field);
                
                if (rowIndex < 0 || columnIndex < 0) {
                    return false;
                }
                
                let mainScroller = mainScrollerRef.current;
                const container = containerRef.current;
                
                if (!mainScroller && container) {
                    mainScroller = container.querySelector('.freeze-pane-main .MuiDataGrid-virtualScroller') as HTMLElement
                        || container.querySelector('.freeze-pane-simple .MuiDataGrid-virtualScroller') as HTMLElement
                        || container.querySelector('.MuiDataGrid-virtualScroller') as HTMLElement;
                    
                    if (mainScroller) {
                        mainScrollerRef.current = mainScroller;
                    }
                }
                
                if (!mainScroller) {
                    return false;
                }

                const cellTop = rowIndex * rowHeight;
                let cellLeft = 0;
                for (let i = 0; i < columnIndex; i++) {
                    cellLeft += columns[i].width || columns[i].minWidth || 150;
                }

                let scrollTop: number;
                let scrollLeft: number;

                if (scrollToCell.viewportOffset) {
                    const rowOffset = scrollToCell.viewportOffset.top;
                    const columnOffset = scrollToCell.viewportOffset.left;
                    
                    const offsetTop = rowOffset * rowHeight;
                    let offsetLeft = 0;
                    for (let i = 0; i < columnOffset; i++) {
                        offsetLeft += columns[i].width || columns[i].minWidth || 150;
                    }
                    
                    scrollTop = cellTop - offsetTop;
                    scrollLeft = cellLeft - offsetLeft;
                } else {
                    scrollTop = cellTop;
                    scrollLeft = cellLeft;
                }

                mainScroller.scrollTop = Math.max(0, scrollTop);
                mainScroller.scrollLeft = Math.max(0, scrollLeft);
                
                return true;
            } catch (error) {
                return false;
            }
        };

        const timeoutId = setTimeout(() => {
            scrollToCellTopLeft();
        }, 50);
        
        requestAnimationFrame(() => {
            if (!scrollToCellTopLeft()) {
                setTimeout(() => {
                    scrollToCellTopLeft();
                }, 100);
            }
        });

        return () => clearTimeout(timeoutId);
    }, [scrollToCell, rows, columns, rowHeight]);

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        let cleanupFn: (() => void) | null = null;
        let retryCount = 0;
        const MAX_RETRIES = 20;

        const setupScrollSync = () => {
            const mainScroller = container.querySelector('.freeze-pane-main .MuiDataGrid-virtualScroller') as HTMLElement;
            const frozenColsScroller = container.querySelector('.freeze-pane-columns .MuiDataGrid-virtualScroller') as HTMLElement;
            const frozenRowsScroller = container.querySelector('.freeze-pane-rows .MuiDataGrid-virtualScroller') as HTMLElement;

            if (!mainScroller) {
                retryCount++;
                if (retryCount < MAX_RETRIES) {
                    requestAnimationFrame(() => {
                        if (setupScrollSync()) {
                            retryCount = 0;
                        }
                    });
                }
                return false;
            }

            retryCount = 0;
            mainScrollerRef.current = mainScroller;
            frozenColsScrollerRef.current = frozenColsScroller;
            frozenRowsScrollerRef.current = frozenRowsScroller;

            const createImmediateScrollHandler = (source: ScrollSource, getPosition: () => { scrollTop: number; scrollLeft: number }) => {
                return () => {
                    const now = performance.now();
                    const lock = scrollLockRef.current;

                    if (lock && lock.source !== source && (now - lock.timestamp) < SCROLL_LOCK_DURATION) {
                        return;
                    }

                    scrollLockRef.current = { source, timestamp: now };

                    const { scrollTop, scrollLeft } = getPosition();

                    const lastPos = lastSyncedPositionRef.current;
                    if (Math.abs(scrollTop - lastPos.scrollTop) < 0.5 &&
                        Math.abs(scrollLeft - lastPos.scrollLeft) < 0.5) {
                        return;
                    }

                    syncScrollPositions(source, scrollTop, scrollLeft);
                };
            };

            const handleMainScroll = createImmediateScrollHandler(ScrollSource.Main, () => ({
                scrollTop: mainScroller.scrollTop,
                scrollLeft: mainScroller.scrollLeft
            }));

            const handleFrozenColsScroll = createImmediateScrollHandler(ScrollSource.FrozenCols, () => ({
                scrollTop: frozenColsScroller?.scrollTop ?? 0,
                scrollLeft: frozenColsScroller?.scrollLeft ?? 0
            }));

            const handleFrozenRowsScroll = createImmediateScrollHandler(ScrollSource.FrozenRows, () => {
                const mainScrollTop = mainScroller?.scrollTop ?? 0;
                return {
                    scrollTop: mainScrollTop,
                    scrollLeft: frozenRowsScroller?.scrollLeft ?? 0
                };
            });

            const handleScrollEnd = () => {
                forceAlignmentSync();
            };

            mainScroller.addEventListener('scroll', handleMainScroll, { passive: true });
            if (frozenColsScroller && frozenColumnCount > 0) {
                frozenColsScroller.addEventListener('scroll', handleFrozenColsScroll, { passive: true });
            }
            if (frozenRowsScroller && frozenRowCount > 0) {
                frozenRowsScroller.addEventListener('scroll', handleFrozenRowsScroll, { passive: true });
            }

            if ('onscrollend' in window) {
                mainScroller.addEventListener('scrollend', handleScrollEnd, { passive: true });
            }

            cleanupFn = () => {
                mainScroller.removeEventListener('scroll', handleMainScroll);
                if (frozenColsScroller) frozenColsScroller.removeEventListener('scroll', handleFrozenColsScroll);
                if (frozenRowsScroller) frozenRowsScroller.removeEventListener('scroll', handleFrozenRowsScroll);
                if ('onscrollend' in window) {
                    mainScroller.removeEventListener('scrollend', handleScrollEnd);
                }
            };

            return true;
        };

        if (!setupScrollSync()) {
            const observer = new MutationObserver(() => {
                if (setupScrollSync()) {
                    observer.disconnect();
                }
            });

            observer.observe(container, { childList: true, subtree: true });

            return () => {
                observer.disconnect();
                if (rafIdRef.current !== null) {
                    cancelAnimationFrame(rafIdRef.current);
                }
                cleanupFn?.();
            };
        }

        return () => {
            if (rafIdRef.current !== null) {
                cancelAnimationFrame(rafIdRef.current);
            }
            cleanupFn?.();
        };
    }, [frozenColumnCount, frozenRowCount, syncScrollPositions, forceAlignmentSync]);

    useEffect(() => {
        if (frozenRowCount === 0) return;

        const container = containerRef.current;
        if (!container) return;

        const removeTopContainers = () => {
            const frozenColsTopContainer = container.querySelector('.freeze-pane-columns .MuiDataGrid-topContainer');
            const mainTopContainer = container.querySelector('.freeze-pane-main .MuiDataGrid-topContainer');

            if (frozenColsTopContainer) {
                frozenColsTopContainer.remove();
            }
            if (mainTopContainer) {
                mainTopContainer.remove();
            }
        };

        removeTopContainers();

        const observer = new MutationObserver(() => {
            removeTopContainers();
        });

        observer.observe(container, {
            childList: true,
            subtree: true,
        });

        return () => {
            observer.disconnect();
        };
    }, [frozenRowCount]);

    const getCellClassName = useCallback((params: GridCellParams): string => {
        if (selectedCell && 
            selectedCell.id === params.id && 
            selectedCell.field === params.field) {
            return 'freeze-pane-selected-cell';
        }
        return '';
    }, [selectedCell]);

    const baseGridProps = useMemo(() => ({
        ...otherProps,
        rowHeight,
        columnHeaderHeight,
        disableColumnFilter: true,
        disableColumnMenu: true,
        hideFooter: true,
        showCellVerticalBorder: true,
        showColumnVerticalBorder: true,
        onCellClick: handleCellClick,
        getCellClassName,
    }), [otherProps, rowHeight, columnHeaderHeight, handleCellClick, getCellClassName]);

    const frozenGridProps = useMemo(() => ({
        ...baseGridProps,
        disableVirtualization: true,
        disableColumnResize: true,
    }), [baseGridProps]);

    const mainGridProps = useMemo(() => ({
        ...baseGridProps,
        columnBufferPx: currentColumnBuffer * 150,
        rowBufferPx: currentRowBuffer * rowHeight,
    }), [baseGridProps, currentColumnBuffer, currentRowBuffer, rowHeight]);

    const hasFrozenColumns = frozenColumnCount > 0;
    const hasFrozenRows = frozenRowCount > 0;

    if (!hasFrozenColumns && !hasFrozenRows) {
        return (
            <div ref={containerRef} className="freeze-pane-container freeze-pane-simple">
                <DataGrid
                    {...mainGridProps}
                    apiRef={mainGridApiRef}
                    columns={columns}
                    rows={rows}
                    sx={mainGridSx}
                />
            </div>
        );
    }

    const gridKey = `${frozenColumnCount}-${frozenRowCount}`;

    return (
        <div ref={containerRef} className="freeze-pane-container">
            {hasFrozenColumns && hasFrozenRows && (
                <div
                    className="freeze-pane-corner"
                    style={{
                        width: frozenColumnsWidth,
                        height: frozenRowsHeight,
                    }}
                >
                    <DataGrid
                        key={`corner-${gridKey}`}
                        {...frozenGridProps}
                        apiRef={frozenCornerApiRef}
                        columns={frozenColumns}
                        rows={frozenRows}
                        sx={frozenCornerSx}
                    />
                </div>
            )}

            {hasFrozenColumns && (
                <div
                    className="freeze-pane-columns"
                    style={{
                        width: frozenColumnsWidth,
                        top: hasFrozenRows ? frozenRowsHeight : 0,
                        height: hasFrozenRows ? `calc(100% - ${frozenRowsHeight}px)` : '100%',
                    }}
                >
                    <DataGrid
                        key={`cols-${gridKey}`}
                        {...frozenGridProps}
                        apiRef={frozenColumnsApiRef}
                        columns={frozenColumns}
                        rows={scrollableRows}
                        columnHeaderHeight={hasFrozenRows ? 0 : columnHeaderHeight}
                        sx={frozenColumnsSx}
                    />
                </div>
            )}

            {hasFrozenRows && (
                <div
                    className="freeze-pane-rows"
                    style={{
                        height: frozenRowsHeight,
                        left: hasFrozenColumns ? frozenColumnsWidth : 0,
                        width: hasFrozenColumns ? `calc(100% - ${frozenColumnsWidth}px)` : '100%',
                    }}
                >
                    <DataGrid
                        key={`rows-${gridKey}`}
                        {...frozenGridProps}
                        apiRef={frozenRowsApiRef}
                        columns={hasFrozenColumns ? scrollableColumns : columns}
                        rows={frozenRows}
                        sx={frozenRowsSx}
                    />
                </div>
            )}

            <div
                className="freeze-pane-main"
                style={{
                    left: hasFrozenColumns ? frozenColumnsWidth : 0,
                    top: hasFrozenRows ? frozenRowsHeight : 0,
                    width: hasFrozenColumns ? `calc(100% - ${frozenColumnsWidth}px)` : '100%',
                    height: hasFrozenRows ? `calc(100% - ${frozenRowsHeight}px)` : '100%',
                }}
            >
                <DataGrid
                    key={`main-${gridKey}`}
                    {...mainGridProps}
                    apiRef={mainGridApiRef}
                    columns={hasFrozenColumns ? scrollableColumns : columns}
                    rows={scrollableRows}
                    columnHeaderHeight={hasFrozenRows ? 0 : columnHeaderHeight}
                    sx={mainGridSx}
                />
            </div>
        </div>
    );
};

export default FreezePaneDataGrid;
